import React from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionCard = ({ subscription, onCancel, onReload }) => {
  if (!subscription) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-foreground mb-2">No Active Subscription</h3>
        <p className="text-muted-foreground mb-6">You don't have an active subscription at the moment.</p>
        <Button onClick={() => window.location.href = '/membership-plan-builder'}>
          <Icon name="Plus" size={18} />
          Browse Membership Plans
        </Button>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      expired: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors?.[status] || colors?.pending;
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: 'CheckCircle',
      pending: 'Clock',
      cancelled: 'XCircle',
      expired: 'AlertCircle'
    };
    return icons?.[status] || 'Clock';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">{subscription?.membership_plans?.plan_name}</h2>
            <p className="text-white/90">Plan Code: {subscription?.membership_plans?.plan_code}</p>
          </div>
          <div className={`px-4 py-2 rounded-full border flex items-center gap-2 ${getStatusColor(subscription?.status)}`}>
            <Icon name={getStatusIcon(subscription?.status)} size={16} />
            <span className="font-semibold capitalize">{subscription?.status}</span>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="p-6 space-y-6">
        {/* Pricing */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-background p-4 rounded-lg border border-border">
            <p className="text-sm text-muted-foreground mb-1">Monthly Price</p>
            <p className="text-2xl font-bold text-foreground">${parseFloat(subscription?.final_price || 0)?.toFixed(2)}</p>
          </div>
          <div className="bg-background p-4 rounded-lg border border-border">
            <p className="text-sm text-muted-foreground mb-1">Billing Cycle</p>
            <p className="text-2xl font-bold text-foreground capitalize">{subscription?.billing_cycle || 'Monthly'}</p>
          </div>
          <div className="bg-background p-4 rounded-lg border border-border">
            <p className="text-sm text-muted-foreground mb-1">Next Billing Date</p>
            <p className="text-2xl font-bold text-foreground">
              {subscription?.next_billing_date ? format(new Date(subscription?.next_billing_date), 'MMM dd, yyyy') : 'N/A'}
            </p>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border">
            <Icon name="Calendar" size={20} className="text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Start Date</p>
              <p className="font-semibold text-foreground">
                {format(new Date(subscription?.start_date), 'MMM dd, yyyy')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border">
            <Icon name="Calendar" size={20} className="text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">End Date</p>
              <p className="font-semibold text-foreground">
                {subscription?.end_date ? format(new Date(subscription?.end_date), 'MMM dd, yyyy') : 'Ongoing'}
              </p>
            </div>
          </div>
        </div>

        {/* Features */}
        {subscription?.membership_plans?.features && (
          <div>
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Star" size={18} className="text-primary" />
              Plan Features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {JSON.parse(subscription?.membership_plans?.features || '[]')?.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Check" size={16} className="text-green-600" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {subscription?.status === 'active' && (
          <div className="pt-4 border-t border-border flex gap-3">
            <Button variant="outline" onClick={onReload}>
              <Icon name="RefreshCw" size={18} />
              Refresh
            </Button>
            <Button variant="destructive" onClick={onCancel}>
              <Icon name="XCircle" size={18} />
              Cancel Subscription
            </Button>
          </div>
        )}

        {subscription?.status === 'cancelled' && subscription?.cancellation_date && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-medium mb-1">Subscription Cancelled</p>
            <p className="text-red-600 text-sm">
              Cancelled on {format(new Date(subscription?.cancellation_date), 'MMM dd, yyyy')}
            </p>
            {subscription?.cancellation_reason && (
              <p className="text-red-600 text-sm mt-1">Reason: {subscription?.cancellation_reason}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCard;